import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Rocket, 
  CalendarDays,
  ArrowRight,
  Sparkles,
  LightbulbIcon,
  BarChart3
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";

interface ProjectStats {
  contentCount: number;
  tasksCompleted: number;
  totalTasks: number;
}

interface ProjectCardProps {
  id: string;
  name: string;
  businessName: string;
  industry: string;
  companyType: string;
  employeeCount: number;
  createdAt: string;
  stats?: ProjectStats;
  isNew?: boolean;
}

export function EnhancedProjectCard({
  id,
  name,
  businessName,
  industry,
  companyType,
  createdAt,
  stats = {
    contentCount: 0,
    tasksCompleted: 0,
    totalTasks: 7,
  },
  isNew = false,
}: ProjectCardProps) {
  const formattedDate = new Date(createdAt);
  const timeAgo = formatDistanceToNow(formattedDate, { addSuffix: true });
  const progress = stats.totalTasks > 0 
    ? Math.round((stats.tasksCompleted / stats.totalTasks) * 100) 
    : 0;
  
  // Display content count in a readable format if it's large
  const formatContentCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };
  
  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="h-full"
    >
      <Link to={`/projects/${id}`} className="block h-full">
        <div className="relative h-full rounded-xl overflow-hidden border border-blue-100 dark:border-blue-800/30 bg-gradient-to-br from-blue-50 to-violet-50 dark:from-blue-950/30 dark:to-violet-950/30 shadow-sm transition-all hover:shadow-lg">
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-pink-500/20 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-all"></div>
          
          {isNew && (
            <div className="absolute -right-12 top-0 bg-gradient-to-r from-blue-600 to-pink-600 text-white px-14 py-1 rotate-45 text-xs font-medium shadow-sm z-10">
              New
            </div>
          )}
          
          {/* Content */}
          <div className="relative p-6 flex flex-col h-full">
            {/* Header */}
            <div className="mb-4">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">{name}</h3>
                  {businessName && businessName !== name && (
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">{businessName}</p>
                  )}
                </div>
                
                {companyType && (
                  <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-0">
                    {companyType}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-3 p-4 mb-4 bg-white/50 dark:bg-zinc-800/50 rounded-lg shadow-sm border border-zinc-100 dark:border-zinc-700/30">
              <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold mr-1.5 text-zinc-900 dark:text-white">{formatContentCount(stats.contentCount)}</span>
                  <span className="text-sm text-zinc-600 dark:text-zinc-300">content pieces</span>
                </div>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">Created in this project</p>
              </div>
            </div>
            
            {/* Progress */}
            {stats.totalTasks > 0 && (
              <div className="mb-4 space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-zinc-600 dark:text-zinc-300 font-medium">Project Progress</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">{progress}%</span>
                </div>
                <div className="h-2 bg-zinc-100 dark:bg-zinc-700/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full bg-gradient-to-r from-blue-600 to-pink-600"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Footer */}
            <div className="mt-auto flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400">
                <CalendarDays className="h-4 w-4" />
                <span>{timeAgo}</span>
              </div>
              
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-blue-600 dark:text-blue-400">Open Project</span>
                <ArrowRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
} 